/**
 * referralReasonAgreement
 * -----------------------
 * Per-side referral-reason endorsement for the clinician's referral summary
 * (LSC-016): what the school said the referral is for, what the parent/carer
 * said, and whether they agree.
 *
 * The stored patientRecord.intakeConcern collapses both answers into one value
 * (disagreement becomes 'both'), destroying the per-side signal — so this
 * reads each side's own answer from its reasonForReferral form instance.
 * The school's question id is `main_reason`; the guardian's live question is
 * `lg_main_concern` (same pathway options, parent-voice wording), with
 * `main_reason` kept as a fallback for instances created from older schema
 * versions that used one id for both sides. A missing answer reads as
 * "not answered" and the agreement stays undecided.
 */

const REASON_OPTION_LABELS = {
  autism_concern: 'Autism',
  adhd_concern: 'ADHD',
  both_concern: 'Autism and ADHD',
  other: 'Other',
};

export const REASON_AGREEMENT = {
  AGREE: 'agree',
  DIFFER: 'differ',
  INCOMPLETE: 'incomplete',
};

// Which pathway(s) each reason option endorses. 'other' endorses neither
// explicitly, so a per-pathway comparison is not derivable for it and the
// whole-answer comparison stands alone.
const OPTION_PATHWAY_ENDORSEMENT = {
  autism_concern: { adhd: false, asd: true },
  adhd_concern: { adhd: true, asd: false },
  both_concern: { adhd: true, asd: true },
};

const PATHWAY_AGREEMENT_LABELS = { adhd: 'ADHD', asd: 'Autism' };

// Answers arrive as a bare optionId string, an { optionId, otherText } object,
// or a single-element array of either (mirrors the backend's option reader).
function extractOption(answer) {
  const value = Array.isArray(answer) ? answer[0] : answer;
  if (typeof value === 'string' && value.trim()) {
    return { optionId: value.trim(), otherText: '' };
  }
  if (value && typeof value === 'object' && typeof value.optionId === 'string' && value.optionId.trim()) {
    return { optionId: value.optionId.trim(), otherText: value.otherText || '' };
  }
  return null;
}

function describe(option) {
  if (!option) return null;
  return {
    optionId: option.optionId,
    label: REASON_OPTION_LABELS[option.optionId] || option.optionId,
    detail: option.optionId === 'other' ? (option.otherText || '').trim() : '',
  };
}

/**
 * @param {Object} formInstances — the patient's form instances keyed by
 *   `${formKey}_${role}` (as subscribed on the provider overview).
 * @returns {{ school: Object|null, guardian: Object|null, status: string,
 *   pathways: Array<{pathway: string, label: string, status: string}>|null }}
 *   status is 'agree' | 'differ' | 'incomplete' (one or both sides missing —
 *   per the 24/07/2026 decision, no indicator is shown for that state).
 *   `pathways` splits a disagreement per pathway (the agreed view: green
 *   where school and parent endorse the same, yellow where they differ —
 *   e.g. school "both" vs parent "ADHD" agrees on ADHD and differs on
 *   autism). Null when either side is missing or chose "other", where the
 *   per-pathway endorsement cannot be derived.
 */
export function getReferralReasonAgreement(formInstances) {
  const school = extractOption(
    formInstances?.reasonForReferral_school_user?.answers?.main_reason,
  );
  const guardianAnswers = formInstances?.reasonForReferral_guardian?.answers;
  // Mirror the backend readers (rms_forms): fall back to the legacy id when
  // the lg answer yields no option - present-but-empty included - never only
  // when the field is missing.
  const guardian = extractOption(guardianAnswers?.lg_main_concern)
    || extractOption(guardianAnswers?.main_reason)
    || null;
  let status = REASON_AGREEMENT.INCOMPLETE;
  let pathways = null;
  if (school && guardian) {
    status = school.optionId === guardian.optionId
      ? REASON_AGREEMENT.AGREE
      : REASON_AGREEMENT.DIFFER;
    const schoolEndorsement = OPTION_PATHWAY_ENDORSEMENT[school.optionId];
    const guardianEndorsement = OPTION_PATHWAY_ENDORSEMENT[guardian.optionId];
    if (schoolEndorsement && guardianEndorsement) {
      pathways = Object.keys(PATHWAY_AGREEMENT_LABELS).map((pathway) => ({
        pathway,
        label: PATHWAY_AGREEMENT_LABELS[pathway],
        status: schoolEndorsement[pathway] === guardianEndorsement[pathway]
          ? REASON_AGREEMENT.AGREE
          : REASON_AGREEMENT.DIFFER,
      }));
    }
  }
  return { school: describe(school), guardian: describe(guardian), status, pathways };
}
